/* eslint-disable @typescript-eslint/naming-convention */
import * as React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Header from '@components/Header';
import Sidebar from '@components/Sidebar';
import LogsPage from '@pages/LogsPage';
import { LoginPage } from './components/LoginPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { getDefaultFilters } from './utils/defaultFilters';
import type { LogFilters } from './types/index';

// Create a React Query client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false
        }
    }
});

// Main app component that renders based on auth status
function AppContent() {
    const { isAuthenticated, isLoading } = useAuth();
    const [sidebarOpen, setSidebarOpen] = React.useState(true);
    const [filters, setFilters] = React.useState<LogFilters>(getDefaultFilters());

    // Show loading spinner while checking auth status
    if (isLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin h-12 w-12 border-4 border-logwatch-blue border-t-transparent rounded-full mx-auto mb-4" />
                    <p className="text-white">Loading...</p>
                </div>
            </div>
        );
    }

    // Show login page if not authenticated
    if (!isAuthenticated) {
        return <LoginPage />;
    }

    // Show main app if authenticated
    return (
        <Router>
            <div className="flex h-screen bg-background">
                {/* Sidebar */}
                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} filters={filters} onFiltersChange={setFilters} />

                {/* Main content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Header */}
                    <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

                    {/* Main content area */}
                    <main className="flex-1 overflow-x-hidden overflow-y-auto bg-background">
                        <Routes>
                            <Route path="/" element={<LogsPage filters={filters} onFiltersChange={setFilters} />} />
                            <Route path="/logs" element={<LogsPage filters={filters} onFiltersChange={setFilters} />} />
                        </Routes>
                    </main>
                </div>
            </div>
        </Router>
    );
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <AppContent />
            </AuthProvider>
        </QueryClientProvider>
    );
}

export default App;
